<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useEmitter } from 'dashboard/composables/emitter';
import todaySiteVisitsAPI from 'dashboard/api/nexceo/todaySiteVisits';

const { accountId } = useAccount();
const router = useRouter();

const data = ref(null);
const isLoading = ref(false);

async function load() {
  isLoading.value = true;
  try {
    const res = await todaySiteVisitsAPI.get();
    data.value = res.data;
  } finally {
    isLoading.value = false;
  }
}

onMounted(load);

// Allow parent to trigger a refresh (e.g. after scheduling a site visit via
// this user's own drawer action) — kept as a fast path, but M26.15.7 makes
// it not the *only* path: this widget previously had no live-update
// subscription at all, so a site visit scheduled/rescheduled/completed by
// another agent, or from a different browser tab, never updated these
// counts until the next full page load. nexceo_site_visit.updated fires
// unconditionally on every SiteVisit create/update (no tracked-fields gate,
// unlike nexceo_lead.updated), so it alone already covers every event that
// can change these counts without the noise of subscribing to every
// unrelated lead field change too.
defineExpose({ load });
useEmitter('nexceo_site_visit.updated', load);

function goToCalendar() {
  router.push({
    name: 'nexceo_calendar',
    params: { accountId: accountId.value },
  });
}
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div
    class="flex items-center gap-3 px-4 py-2 bg-n-background border-b border-n-weak shrink-0 flex-wrap"
  >
    <div class="flex items-center gap-1.5 shrink-0">
      <span class="i-lucide-calendar-clock size-3.5 text-woot-500" />
      <span class="text-xs font-semibold text-n-slate-10">Today's Visits:</span>
    </div>

    <div v-if="isLoading" class="text-xs text-n-slate-7">
      <span
        class="i-lucide-loader-2 size-3 animate-spin inline-block mr-1"
      />Loading…
    </div>

    <template v-else-if="data">
      <!-- Stat chips -->
      <span
        v-if="data.total > 0"
        class="inline-flex items-center gap-1 text-xs font-semibold text-n-slate-11 bg-n-alpha-2 px-2 py-0.5 rounded-full"
      >
        <span class="i-lucide-users size-3" />
        {{ data.total }} total
      </span>

      <span
        v-if="data.scheduled > 0"
        class="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full"
      >
        <span class="i-lucide-clock size-3" />
        {{ data.scheduled }} scheduled
      </span>

      <span
        v-if="data.confirmed > 0"
        class="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-full"
      >
        <span class="i-lucide-check-circle size-3" />
        {{ data.confirmed }} confirmed
      </span>

      <span
        v-if="data.completed > 0"
        class="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full"
      >
        <span class="i-lucide-check-check size-3" />
        {{ data.completed }} done
      </span>

      <span
        v-if="data.cancelled > 0"
        class="inline-flex items-center gap-1 text-xs font-semibold text-red-600 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full"
      >
        <span class="i-lucide-x-circle size-3" />
        {{ data.cancelled }} cancelled
      </span>

      <!-- Next upcoming visit -->
      <span
        v-if="data.next_visit"
        class="inline-flex items-center gap-1 text-xs text-n-slate-9 bg-woot-50 dark:bg-woot-500/10 border border-woot-200 px-2 py-0.5 rounded-full"
      >
        <span class="i-lucide-map-pin size-3 text-woot-500" />
        Next: {{ data.next_visit.lead_name || 'Unnamed' }} at
        {{ data.next_visit.scheduled_at_ist_time }}
      </span>

      <span v-if="data.total === 0" class="text-xs text-n-slate-7">
        No visits today.</span
      >
    </template>

    <button
      class="ml-auto text-xs text-woot-600 hover:text-woot-700 font-medium flex items-center gap-1 shrink-0"
      @click="goToCalendar"
    >
      View calendar
      <span class="i-lucide-arrow-right size-3" />
    </button>
  </div>
</template>
